import React, { useMemo, useState, useEffect } from 'react';
import {
  startOfDay, endOfDay, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  startOfYear, format, isSameDay,
} from 'date-fns';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { CalendarIcon, X } from 'lucide-react';

/**
 * Compact date-range filter styled to the user's reference screenshot.
 *   • Left column — quick presets (Today, Yesterday, Last 7 days, …, Lifetime)
 *   • Right column — a 2-month calendar in `range` mode
 *   • Footer — Cancel / Apply buttons
 *
 * Emits `{ from: Date|null, to: Date|null }` through `onApply`.
 * Passing `null, null` means "no filter" (Lifetime).
 */
const PRESETS = (now = new Date()) => {
  const today = startOfDay(now);
  const todayEnd = endOfDay(now);
  const yesterday = startOfDay(subDays(now, 1));
  return [
    { key: 'today', label: 'Today', range: { from: today, to: todayEnd } },
    { key: 'yesterday', label: 'Yesterday', range: { from: yesterday, to: endOfDay(yesterday) } },
    { key: 'last7', label: 'Last 7 days', range: { from: startOfDay(subDays(now, 6)), to: todayEnd } },
    { key: 'thisWeek', label: 'This week', range: { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) } },
    { key: 'lastWeek', label: 'Last week', range: { from: startOfWeek(subDays(now, 7), { weekStartsOn: 1 }), to: endOfWeek(subDays(now, 7), { weekStartsOn: 1 }) } },
    { key: 'last30', label: 'Last 30 days', range: { from: startOfDay(subDays(now, 29)), to: todayEnd } },
    { key: 'thisMonth', label: 'This month', range: { from: startOfMonth(now), to: endOfMonth(now) } },
    { key: 'lastMonth', label: 'Last month', range: { from: startOfMonth(subDays(startOfMonth(now), 1)), to: endOfMonth(subDays(startOfMonth(now), 1)) } },
    { key: 'ytd', label: 'Year to date', range: { from: startOfYear(now), to: todayEnd } },
    { key: 'lifetime', label: 'Lifetime', range: { from: null, to: null } },
  ];
};

const sameRange = (a, b) => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (!a.from && !b.from) return true;
  if (!a.from || !b.from) return false;
  if (!a.to || !b.to) return false;
  return isSameDay(a.from, b.from) && isSameDay(a.to, b.to);
};

const formatRangeLabel = (range) => {
  if (!range || !range.from) return 'Lifetime';
  if (range.to && !isSameDay(range.from, range.to)) {
    return `${format(range.from, 'MMM d')} – ${format(range.to, 'MMM d, yyyy')}`;
  }
  return format(range.from, 'MMM d, yyyy');
};

export default function DateRangeFilter({ value, onApply, resultCount, buttonTestId = 'date-range-trigger', triggerClassName }) {
  const [open, setOpen] = useState(false);
  // `draft` is the in-popover edit state; only committed on Apply.
  const [draft, setDraft] = useState(value || { from: null, to: null });
  useEffect(() => { if (open) setDraft(value || { from: null, to: null }); }, [open, value]);

  const presets = useMemo(() => PRESETS(), []);
  const activePresetKey = useMemo(() => {
    const match = presets.find((p) => sameRange(p.range, draft));
    return match?.key || null;
  }, [draft, presets]);

  const activeLabel = formatRangeLabel(value);
  const isFiltered = !!(value && value.from);

  const commit = () => {
    onApply?.(draft);
    setOpen(false);
  };
  const clear = (e) => {
    e.stopPropagation();
    onApply?.({ from: null, to: null });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={`h-9 justify-start text-left font-normal gap-2 ${triggerClassName || (isFiltered ? 'border-violet-300 bg-violet-50 text-violet-900 hover:bg-violet-100' : '')}`}
          data-testid={buttonTestId}
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
          <span className="text-[13px]">{activeLabel}</span>
          {isFiltered && typeof resultCount === 'number' && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-violet-600 text-white text-[11px] font-semibold">
              {resultCount}
            </span>
          )}
          {isFiltered && (
            <X
              className="h-3.5 w-3.5 text-slate-400 hover:text-rose-500 ml-1"
              onClick={clear}
              data-testid="date-range-clear"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="p-0 w-auto" sideOffset={6} data-testid="date-range-popover">
        <div className="flex">
          {/* Presets column */}
          <div className="w-40 border-r border-slate-100 py-2 bg-slate-50/40">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setDraft(p.range)}
                className={`w-full text-left px-4 py-1.5 text-[13px] transition ${
                  activePresetKey === p.key
                    ? 'bg-slate-900 text-white font-medium'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
                data-testid={`date-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
            <p className="text-[10px] text-slate-400 px-4 pt-2 pb-1">Asia/Kolkata</p>
          </div>
          {/* Calendar column */}
          <div className="p-3">
            <Calendar
              mode="range"
              numberOfMonths={2}
              selected={draft}
              onSelect={(r) => setDraft(r || { from: null, to: null })}
              defaultMonth={draft?.from || new Date()}
            />
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-testid="date-range-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={commit}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="date-range-apply"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
